import { z } from 'zod';

/**
 * De accounts van een organisatie zoals Master ze ziet (MST-2).
 *
 * Een account, geen persoon: naam, aanmeldgegevens, status en de laatste aanmelding. Wat iemand in
 * de organisatie doet (rollen, groepen, velden), beheert de organisatie zelf in haar eigen beheer.
 */
export const platformAccountSchema = z.object({
  id: z.guid(),
  personId: z.guid(),
  displayName: z.string(),
  loginEmail: z.string().nullable(),
  employeeNumber: z.string().nullable(),
  status: z.enum(['pending_activation', 'active', 'disabled']),
  lastLoginAt: z.string().datetime().nullable(),
});
export type PlatformAccount = z.infer<typeof platformAccountSchema>;

export const platformAccountListSchema = z.object({
  items: z.array(platformAccountSchema),
});
export type PlatformAccountList = z.infer<typeof platformAccountListSchema>;

/**
 * Hoeveel accounts een organisatie heeft, en hoe actief ze zijn (MST-5). De basis voor de lijst in
 * Master, de kerncijfers en de upsell-analyse (MST-6).
 */
export const tenantAccountStatsSchema = z.object({
  tenantId: z.guid(),
  total: z.number().int(),
  active: z.number().int(),
  pending: z.number().int(),
  disabled: z.number().int(),
  /** Aangemeld in de laatste 7 en 30 dagen. */
  active7d: z.number().int(),
  active30d: z.number().int(),
  /** Aangemaakt in de laatste 30 dagen. */
  new30d: z.number().int(),
  lastLoginAt: z.string().datetime().nullable(),
});
export type TenantAccountStats = z.infer<typeof tenantAccountStatsSchema>;

export const tenantAccountStatsListSchema = z.object({ items: z.array(tenantAccountStatsSchema) });
export type TenantAccountStatsList = z.infer<typeof tenantAccountStatsListSchema>;

// --- MST-7: platformbeheerders -----------------------------------------------------------------

/** Een beheerder van het platform zelf, zoals Master hem toont. Zonder hash of TOTP-geheim. */
export const platformAdminSchema = z.object({
  id: z.guid(),
  email: z.string(),
  displayName: z.string(),
  /** Heeft een tweede factor ingesteld. */
  mfa: z.boolean(),
  lastLoginAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  disabledAt: z.string().datetime().nullable(),
});
export type PlatformAdmin = z.infer<typeof platformAdminSchema>;

export const platformAdminListSchema = z.object({ items: z.array(platformAdminSchema) });
export type PlatformAdminList = z.infer<typeof platformAdminListSchema>;

/**
 * Een beheerder toevoegen met een tijdelijk wachtwoord. Hij stelt zelf geen tweede factor in vanuit
 * Master; dat blijft een stap op de server tot er een scherm voor is.
 */
export const createPlatformAdminRequestSchema = z.object({
  email: z.string().email().max(320),
  displayName: z.string().min(2).max(120),
  password: z.string().min(12).max(200),
});
export type CreatePlatformAdminRequest = z.infer<typeof createPlatformAdminRequestSchema>;
